import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    CustomFlowbiteTheme,
    TextInput as FlowBiteTextInput,
    TextInputProps as FlowBiteTextInputProps,
} from 'flowbite-react';
import { textInputTheme } from '../../styles/themes/textInput';
import { Label } from '../label';
import { useDebounce } from '../../hooks/use-debounce';
import { IconLoader, type IconName } from '../iconLoader';

export type InputType = 'text' | 'email' | 'password' | 'search' | 'url' | 'number' | 'time';
export type InputValue = string;
export type InputStep = number | 'any'; // 'any' for time type

export interface InputProps extends Pick<FlowBiteTextInputProps, 'sizing' | 'disabled' | 'helperText' | 'placeholder'> {
    value?: InputValue;
    type?: InputType;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onDebouncedChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    debounceTime?: number;
    testId?: string;
    id?: string;
    labelValue?: string;
    labelTestId?: string;
    ariaLabel?: string;
    leftIcon?: IconName;
    rightIcon?: IconName;
    onLeftIconClick?: () => void;
    onRightIconClick?: () => void;
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    min?: number | string; // string for time
    max?: number | string; // string for time
    step?: InputStep;
    showErrorMessages?: boolean;
    inputBaseClassName?: string;
}

export const Input: React.FC<InputProps> = (props) => {
    const {
        value,
        type = 'text',
        onChange,
        onDebouncedChange,
        debounceTime = 300,
        leftIcon,
        testId,
        id,
        labelValue,
        labelTestId,
        ariaLabel,
        onRightIconClick,
        onLeftIconClick,
        sizing = 'sm',
        disabled = false,
        helperText = '',
        placeholder,
        rightIcon,
        required = false,
        minLength,
        maxLength,
        min,
        max,
        step = 1,
        showErrorMessages = false,
        inputBaseClassName,
    } = props;

    const [errorMessage, setErrorMessage] = useState<string>('');

    const errors = useMemo(
        () => ({
            required: 'A value is required',
            // text
            wrongLength: `Must be between ${minLength} and ${maxLength} characters`,
            tooShort: `Not less than ${minLength} characters`,
            tooLong: `Not more than ${maxLength} characters`,
            // number
            outOfRange: `Must be between ${min} and ${max}`,
            underMin: `Not smaller than ${min}`,
            overMax: `Not greater than ${max}`,
        }),
        [minLength, maxLength, min, max],
    );

    const validate = useCallback(
        (value: InputValue, type: InputType) => {
            switch (true) {
                // required case
                case required && !value.length:
                    setErrorMessage(errors.required);
                    break;
                // text cases
                case type !== 'number' &&
                    minLength !== undefined &&
                    maxLength !== undefined &&
                    (String(value).length < minLength || String(value).length > maxLength):
                    setErrorMessage(errors.wrongLength);
                    break;
                case type !== 'number' && minLength !== undefined && String(value).length < minLength:
                    setErrorMessage(errors.tooShort);
                    break;
                case type !== 'number' && maxLength !== undefined && String(value).length > maxLength:
                    setErrorMessage(errors.tooLong);
                    break;
                // number cases
                case type === 'number' &&
                    min !== undefined &&
                    max !== undefined &&
                    (Number(value) < Number(min) || Number(value) > Number(max)):
                    setErrorMessage(errors.outOfRange);
                    break;
                case type === 'number' && min !== undefined && Number(value) < Number(min):
                    setErrorMessage(errors.underMin);
                    break;
                case type === 'number' && max !== undefined && Number(value) > Number(max):
                    setErrorMessage(errors.overMax);
                    break;
                // no error case
                default:
                    setErrorMessage('');
            }
        },
        [
            required,
            errors.required,
            errors.wrongLength,
            errors.tooShort,
            errors.tooLong,
            errors.outOfRange,
            errors.underMin,
            errors.overMax,
            minLength,
            maxLength,
            min,
            max,
        ],
    );

    useEffect(() => {
        if (required && !value?.length) setErrorMessage(errors.required);
    }, [errors.required, required, value]);

    const errorState = Boolean(errorMessage);
    const stateThemeColour = errorState ? 'failure' : 'gray';
    const iconColorClasses = errorState
        ? 'fill-system-error stroke-system-error'
        : 'fill-system-on-surface-variant stroke-system-on-surface-variant';

    const SelectedLeftIcon = () => (
        <IconLoader
            className={`shrink-0 ${iconColorClasses}`}
            size={'sm'}
            iconName={leftIcon as IconName}
            onClick={onLeftIconClick}
        />
    );
    const SelectedRightIcon = () => (
        <IconLoader
            className={`shrink-0 ${iconColorClasses}`}
            size={'sm'}
            iconName={rightIcon as IconName}
            onClick={onRightIconClick}
        />
    );

    const themeWithIconClasses: CustomFlowbiteTheme['textInput'] = {
        ...textInputTheme,
        field: {
            ...textInputTheme?.field,
            // make icon fill class conditional
            icon: {
                ...textInputTheme?.field?.icon,
                svg: `${textInputTheme?.field?.icon?.svg} ${iconColorClasses}`,
            },
            // enable style overrides on input.base element
            input: {
                ...textInputTheme?.field?.input,
                base: `${textInputTheme?.field?.input?.base} ${inputBaseClassName}`,
            },
        },
    };

    const debouncedOnChange = useDebounce((event: ChangeEvent<HTMLInputElement>) => {
        if (onDebouncedChange) {
            onDebouncedChange(event);
        }
    }, debounceTime);

    const handleOnChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value;
            validate(newValue, type);
            // Immediate state updates
            if (onChange) {
                onChange(event);
            }
            // Debounced side effects
            if (onDebouncedChange) {
                debouncedOnChange(event);
            }
        },
        [debouncedOnChange, onChange, onDebouncedChange, type, validate],
    );

    const ref = useRef(null);

    const HelperText = () => (
        <span className={errorState ? 'text-system-error' : 'text-system-on-surface-variant'}>
            {showErrorMessages && errorState ? errorMessage : helperText}
        </span>
    );

    return (
        <div className={`font-sans`} ref={ref}>
            {labelValue ? (
                <div className='mb-2'>
                    <Label
                        value={labelValue}
                        color={stateThemeColour}
                        disabled={disabled}
                        testId={labelTestId}
                        componentId={id}
                    />
                </div>
            ) : null}
            <FlowBiteTextInput
                type={type}
                value={value}
                onChange={handleOnChange}
                id={id} // used for label too
                data-testid={testId}
                aria-label={ariaLabel}
                color={stateThemeColour}
                sizing={sizing}
                disabled={disabled}
                placeholder={placeholder}
                icon={leftIcon ? SelectedLeftIcon : undefined}
                rightIcon={rightIcon ? SelectedRightIcon : undefined}
                helperText={<HelperText />}
                required={required}
                minLength={minLength}
                maxLength={maxLength}
                min={min}
                max={max}
                step={step}
                theme={themeWithIconClasses}
            />
        </div>
    );
};
